import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, Lock, Activity, Radio, MapPin } from 'lucide-react';

interface CookieSettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAcceptAll: () => void;
    onRejectAll: () => void;
}

const CookieSettingsModal: React.FC<CookieSettingsModalProps> = ({
    isOpen,
    onClose,
    onAcceptAll,
    onRejectAll,
}) => {
    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                    />
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        className="relative w-full max-w-2xl bg-white dark:bg-gray-900 rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[90vh]"
                    >
                        {/* Header */}
                        <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between bg-white dark:bg-gray-900 sticky top-0 z-10">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                    <Shield className="w-6 h-6 text-primary" />
                                    Paramètres de confidentialité
                                </h2>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                    Gérez vos préférences en matière de cookies
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                            >
                                <X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="p-6 overflow-y-auto space-y-6">
                            <div className="space-y-4">
                                <p className="text-sm text-gray-600 dark:text-gray-300">
                                    La SAPI respecte votre vie privée. Voici comment nous utilisons vos données :
                                </p>

                                {/* Categories */}
                                <div className="space-y-4">
                                    <Category
                                        icon={<Lock className="w-5 h-5 text-green-500" />}
                                        title="Nécessaires"
                                        description="Cookies indispensables au fonctionnement du site. Ils ne peuvent pas être désactivés (ex: connexion, sécurité)."
                                        required
                                    />
                                    <Category
                                        icon={<MapPin className="w-5 h-5 text-blue-500" />}
                                        title="Géolocalisation & Identification"
                                        description="Données de géolocalisation précises et identification par analyse de l’appareil utilisé."
                                    />
                                    <Category
                                        icon={<Activity className="w-5 h-5 text-purple-500" />}
                                        title="Mesure d'audience"
                                        description="Statistiques de visite et analyses pour améliorer nos services."
                                    />
                                    <Category
                                        icon={<Radio className="w-5 h-5 text-orange-500" />}
                                        title="Développement de services"
                                        description="Utilisation des données pour tester et améliorer de nouvelles fonctionnalités."
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="p-6 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 flex flex-col sm:flex-row gap-3 justify-end">
                            <button
                                onClick={onRejectAll}
                                className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                Tout refuser
                            </button>
                            <button
                                onClick={onAcceptAll}
                                className="px-4 py-2.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-hover shadow-lg shadow-primary/20 transition-all hover:scale-[1.02]"
                            >
                                Tout accepter
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

const Category: React.FC<{
    icon: React.ReactNode;
    title: string;
    description: string;
    required?: boolean;
}> = ({ icon, title, description, required }) => (
    <div className="flex gap-4 p-4 rounded-xl border border-gray-100 dark:border-gray-800 hover:border-primary/20 hover:bg-primary/5 transition-all bg-white dark:bg-gray-800/40">
        <div className="flex-shrink-0 mt-1">{icon}</div>
        <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    {title}
                </h3>
                {required && (
                    <span className="text-xs font-medium text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-2 py-0.5 rounded-full">
                        Toujours actif
                    </span>
                )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                {description}
            </p>
        </div>
    </div>
);

export default CookieSettingsModal;
